import React, { useEffect, useState, useCallback } from "react";
import ProductCard from "./ProductCard";
import ProductSkeleton from "./ProductSkeleton";
import { getProducts } from "../services/api";
import { Loader2, Zap } from "lucide-react";
import { motion } from "framer-motion";

const ITEMS_PER_LOAD = 8;

export default function ProductGrid({ filter, search }) {
  const [products, setProducts] = useState([]);
  const [visible, setVisible] = useState(ITEMS_PER_LOAD);
  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // ================= FETCH PRODUCTS =================
  useEffect(() => {
    setLoading(true);
    getProducts().then((res) => {
      setProducts(Array.isArray(res) ? res : []);
      setLoading(false);
    });
  }, []);

  // ================= RESET SCROLL ON FILTER =================
  useEffect(() => {
    setVisible(ITEMS_PER_LOAD);
  }, [filter, search]);

  // ================= FILTER LOGIC =================
  const filtered = products.filter((p) => {
    const matchCategory =
      filter === "All" || p?.category?.name === filter;

    const matchSearch =
      p?.name?.toLowerCase().includes(search.toLowerCase());

    return matchCategory && matchSearch;
  });

  const visibleProducts = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  // ================= INFINITE SCROLL =================
  const loadMore = useCallback(() => {
    if (hasMore && !isFetchingMore) {
      setIsFetchingMore(true);
      setTimeout(() => {
        setVisible((v) => v + ITEMS_PER_LOAD);
        setIsFetchingMore(false);
      }, 400);
    }
  }, [hasMore, isFetchingMore]);

  useEffect(() => {
    const onScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 500
      ) {
        loadMore();
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [loadMore]);

  // ================= ANIMATION =================
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 },
    },
  };

  // ================= RENDER =================
  return (
    <section className="w-full py-10">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-extrabold text-[#7B2E58] relative inline-block">
          {filter === "All" ? "The Entire Collection" : filter}
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            className="absolute bottom-0 left-0 h-1 bg-[#FF80AB] rounded-full"
          />
        </h2>
        <p className="text-gray-500 mt-2">
          Discover hand-picked items just for you.
        </p>
      </div>

      {loading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {[...Array(ITEMS_PER_LOAD)].map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        >
          {visibleProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </motion.div>
      )}

      {!loading && hasMore && isFetchingMore && (
        <div className="flex justify-center mt-8 text-pink-400 font-semibold">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading more sparkle...
        </div>
      )}

      {!loading && !hasMore && filtered.length > 0 && (
        <div className="text-center mt-10 text-gray-400">
          <Zap className="inline mr-1 text-pink-500" />
          That’s all ✨
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center mt-10 p-10 bg-pink-50 rounded-xl text-[#7B2E58] font-semibold">
          No products found.
        </div>
      )}
    </section>
  );
}
